import {
    Site,
    SuperbetRouteConfig,
    NavigationContext,
} from '@superbet-group/web.types';
import { map as _map } from 'lodash';
import Vue from 'vue';
import {
    Location,
    RawLocation,
    Route,
} from 'vue-router';

import { GenericBottomNav } from '@app/mobile/subnavs';
import router from '@app/router';
import { RouteName } from '@app/router/constants/RouteName';
import { isFirstTimeVisit } from '@models/user/UserDataService';
import { openHelp } from '@modules/help';
import FooterComponent from '@modules/layout/footer/components/FooterComponent.vue';
import HeaderContainer from '@modules/layout/header/HeaderContainer.vue';
import SdsMobileHeader from '@app/mobile/navbar/SdsMobileHeader.vue';
import ServiceMessageContainer from '@modules/shared/banners/ServiceMessageContainer.vue';
import SuperSocialMobileHeader from '@socialFederation/shared/SuperSocialMobileHeader.vue';
import TicketsWidget from '@shared/tickets/TicketsWidgetContainer.vue';
import { getTimeFilterFromSlug } from '@shared/timeFilter/helpers';
import { TimeFilter } from '@shared/timeFilter/types';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { errorGuard } from '@utils/ErrorGuard';

import store from '../store';
import { currentSite } from '@app/utils/domainHelpers';
import {
    getConfig,
    IntroPageType,
} from '@superbet-group/web.lib.config';
import { Store } from 'vuex';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { isBelgium } from '@modules/layout/utils/isCountry';
import { retrieveLanderCookie } from '@src/app/utils/landerAnalytics';
import { nextTick } from '@vue/composition-api';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { amIPrerender } from '@app/utils/botUtils';
import {
    applyTheme,
    setHtmlTheme,
} from '../shared/theme/theme';
import StickyLoginRegisterButtons from '@modules/authentication/components/StickyLoginRegisterButtons.vue';
import { Theme } from '@superbet-group/web.lib.ui-components';
import { isSuperSocialMainProduct } from '@socialFederation/helpers';

export const defaultLayout = {
    header: HeaderContainer,
    warning: ServiceMessageContainer,
    mobileHeader: isSuperSocialMainProduct() ? SuperSocialMobileHeader : SdsMobileHeader,
    footer: FooterComponent,
    // TODO: check why dynamic import is not working
    tickets: TicketsWidget,
    mobileBottomSticky: StickyLoginRegisterButtons,
    mobileBottomNav: GenericBottomNav,
};

export function loginRequired(redirectTo: Location = { name: RouteName.App.NotAuthorized }): (to: Route, from: Route, next: AnyFunction) => Promise<void> {
    return async (to: Route, from: Route, next: AnyFunction) => {
        async function action() {
            const user = store.getters['data/authentication/user'];
            const authenticated = await store.dispatch('data/authentication/isAuthenticated');

            if (authenticated) {
                next();
            } else if (user && !authenticated) {
                store.dispatch('data/authentication/sessionExpired');
                next(redirectTo);
            } else {
                store.dispatch('navigation/saveLoginGuardedRoute', to);
                next(redirectTo);
            }
        }
        await errorGuard(action, { showGlobalError: false });
    };
}

export function guestsOnly(
    redirectTo: RawLocation | false | ((vm: Vue) => any) | void = { name: RouteName.App.Home, replace: true },
) {
    return async (to: Route, from: Route, next: AnyFunction) => {
        async function action() {
            const authenticated = await store.dispatch('data/authentication/isAuthenticated');
            if (authenticated) {
                next(redirectTo);
            } else {
                next();
            }
        }
        await errorGuard(action, { showGlobalError: false });
    };
}

/**
 * This helper function chains guards so we can use multiple guards.
 *
 * For example:
 *
 * beforeEnter: (chainGuards)([loginRequired, isAdmin])
 *
 */
export function chainGuards(guards: any) {
    if (!Array.isArray(guards)) {
        throw new Error('You must specify an array of guards');
    }

    return (to: Route, from: Route, next: AnyFunction) => {
        return evaluateGuards(guards, to, from, next);
    };
}

export function isUndefined(value: any) {
    return value === undefined;
}

export function evaluateGuards(guards: AnyFunction[], to: Route, from: Route, next: AnyFunction) {
    const guardsLeft = guards.slice(0);
    const nextGuard = guardsLeft.shift();

    if (isUndefined(nextGuard)) {
        next();
        return;
    }

    nextGuard!(to, from, (nextArg: any) => {
        if (isUndefined(nextArg)) {
            evaluateGuards(guardsLeft, to, from, next);
            return;
        }

        next(nextArg);
    });
}

/* eslint-disable no-param-reassign */
// mutates original
export function addLoginRequirements(route: SuperbetRouteConfig): SuperbetRouteConfig {
    route.meta = {
        ...route.meta,
        isBehindLogin: true,
    };

    if (route.beforeEnter) {
        route.beforeEnter = chainGuards([loginRequired(), route.beforeEnter]);
    } else {
        route.beforeEnter = loginRequired();
    }
    return route;
}
/* eslint-enable no-param-reassign */

export function isBehindLogin(route: Route) {
    return route.meta && route.meta.isBehindLogin;
}

export function checkChatHash(route: Route, next: AnyFunction) {
    if (route.hash === '#chat' && shellLocalConfig.help.canOpenChatViaUrlHash) {
        openHelp();
        next({
            params: route.params,
            path: route.path,
            query: route.query,
        });
    }
}

export function checkIntroPageRedirection(to: Route, next: AnyFunction) {
    const hasParams = _map(to.params).some((param) => param);
    const isReturningUserDevice = !!store.getters['data/authentication/isReturningUserDevice'];
    if (
        getConfig().introPageSetup.type === IntroPageType.Disabled
        || currentSite === Site.NapoleonSports
        || currentSite === Site.NapoleonDice
        || currentSite === Site.NapoleonCasino
        || hasParams
        || (isReturningUserDevice && getConfig().introPageSetup.type === IntroPageType.ShowOnce)
        || getConfig().introPageSetup.type === IntroPageType.ShowOnce && !isFirstTimeVisit()
        || amIPrerender()
    ) {
        next();
        return;
    }

    next({ name: RouteName.GrowthMarketing.LandingIntro });
}

export function shouldOpenLoginModal(to: Route) {
    // WHY : we need a simple solution for redirects to Login, redirects that come from external sources (ex : landing promo pages)
    // if the need for a dedicated Login page will occur, we will adopt an encapsulated login solution (route + component)
    if (to.query.login !== undefined) {
        const query = { ...to.query };
        delete query.login;

        if (!to.meta?.isBehindLogin) {
            router.replace({ path: to.path, query });
            const user = store.getters['data/authentication/user'];
            if (!user) {
                store.dispatch(
                    'ui/modal/setModal',
                    {
                        code: modalCodes.login,
                        isUnique: true,
                    },
                    { root: true },
                );
            }
        }
    }
}

// Exposes a way for native apps to pop and use web forgot password flow, added for brazil mfa-non mfa support
export function maybeOpenForgotPasswordModal(route: Route) {
    if (route.query['forgot-password'] !== undefined) {
        store.dispatch(
            'ui/modal/setModal',
            {
                code: modalCodes.forgotPassword,
                isUnique: true,
            },
            { root: true },
        );
    }
}

export function getSportPageClasses(route: Route) {
    if (TimeFilter.Live === getTimeFilterFromSlug(route.params.timeFilter)) {
        return 'sports-page live';
    } else {
        return 'sports-page';
    }
}

export function getLiveStatusFromQuery(route: Route) {
    // Casting to string as we don't expect an array of strings here
    const queryTimeFilter = route.query?.t as string;

    // We're matching for non-localized values like 'P-superLive', 'offer-live', etc. here
    return queryTimeFilter?.length > 0 && queryTimeFilter.toLocaleLowerCase().includes('live');
}

export function getOfferEventClasses() {
    const isLive = store.getters['navigation/isOnOfferEventLivePage'];
    if (isLive) {
        return 'sports-page live';
    } else {
        return 'sports-page';
    }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function resolveNavigationContext(to: Route, store: Store<unknown>) {
    if (to.name) {
        const currentNavigationContext: NavigationContext = to.meta?.navigationContext;
        if (currentNavigationContext) {
            const navigationContextToSet = currentNavigationContext === NavigationContext.Neutral
                ? shellLocalConfig.neutralNavigationContext!
                : currentNavigationContext;
            store.dispatch(
                'navigation/setCurrentNavigationContext',
                navigationContextToSet);

            setBrowserHeaderColor(navigationContextToSet);
            applyTheme(navigationContextToSet);
        } else {
            if (currentSite === Site.NapoleonSports) {
                store.dispatch(
                    'navigation/setCurrentNavigationContext',
                    NavigationContext.Sports);
                setBrowserHeaderColor(NavigationContext.Sports);
                // free 2 play games are hosted on sports site and force dark theme
                const forcedTheme = to.meta?.forcedTheme;
                if (forcedTheme) {
                    setHtmlTheme(forcedTheme);
                } else {
                    applyTheme(NavigationContext.Sports);
                }
            } else if (isBelgium()) {
                store.dispatch(
                    'navigation/setCurrentNavigationContext',
                    NavigationContext.Casino);
                setBrowserHeaderColor(NavigationContext.Sports);
                const forcedTheme = to.meta?.forcedTheme;
                if (forcedTheme) {
                    setHtmlTheme(forcedTheme);
                } else {
                    applyTheme(NavigationContext.Sports);
                }
            } else {
                const defaultNavigationContext = localStorage.getItem('currentNavigationContext') as NavigationContext ?? NavigationContext.Sports;
                setBrowserHeaderColor(defaultNavigationContext);
                const forcedTheme: Theme = to.meta?.forcedTheme;
                if (forcedTheme) {
                    setHtmlTheme(forcedTheme);
                } else {
                    applyTheme(NavigationContext.Sports);
                }
            }
        }
    }
}

function setBrowserHeaderColor(_navigationContext: NavigationContext) {
    setTimeout(() => {
        const color = getComputedStyle(document.documentElement)
            .getPropertyValue('--gradients-component-global-header-on-body-start-color')
            .trim();
        const metaThemeColor = document.querySelector('[name=theme-color]');
        if (metaThemeColor) {
            metaThemeColor.setAttribute('content', color);
        } else {
            const newMetaTag = document.createElement('meta');
            newMetaTag.setAttribute('name', 'theme-color');
            newMetaTag.setAttribute('content', color);
            document.head.appendChild(newMetaTag);
        }
    }, 1);
}

export function sendLanderAnalytics(to: Route, _from: Route, next: AnyFunction) {
    if (to.query?.sb_ref_source) {
        nextTick(() => {
            AnalyticsSDK.getInstance().log('content_lander_transition', {
                content_lander_hash: retrieveLanderCookie(),
            });
        });
    }
    next();
}
